// Caracal TV Art (16 October 2026): on computers with a mouse, a product card plays its video on hover,
// muted and looped, as on Etsy. The video loads only when the mouse is on the card: attaching it when the card
// came near the screen made Chrome fetch 5.5 MB of video on one collection page. Phones keep the photo.
if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
  const video = (event) => event.target.matches?.('.card-gallery') && event.target.querySelector('video.card-hover-video');
  addEventListener(
    'pointerenter',
    (event) => {
      const v = video(event);
      if (!v) return;
      v.src ||= v.dataset.src;
      v.play().catch(() => {});
    },
    true
  );
  addEventListener(
    'pointerleave',
    (event) => {
      const v = video(event);
      if (!v) return;
      v.pause();
      v.currentTime = 0;
      v.classList.remove('is-playing');
    },
    true
  );
  // Show the video only when it really plays, so a slow line never shows a black box.
  addEventListener('playing', (event) => event.target.classList?.contains('card-hover-video') && event.target.classList.add('is-playing'), true);
}

// Cart: the download consent box (snippets/cart-summary.liquid) says in plain words why checkout waits.
addEventListener(
  'invalid',
  (event) => {
    if (event.target.name === 'attributes[Download consent]') {
      event.target.setCustomValidity('Please tick the box to continue to checkout.');
    }
  },
  true
);
addEventListener('change', (event) => {
  if (event.target.name === 'attributes[Download consent]') event.target.setCustomValidity('');
});

// Product lists with "Load more" (sections/product-list.liquid, home "All art"): the next page of artworks is added
// under the grid when the visitor nears the end or presses the button, until the last artwork. Nobody leaves the page.
document.querySelectorAll('.load-more').forEach((box) => {
  const grid = box.parentElement.querySelector('.resource-list');
  const button = box.querySelector('button');
  // From 990 px wide the theme scrolls .page-wrapper, not the window (assets/base.css).
  const wrapper = document.querySelector('.page-wrapper');
  const root = wrapper && getComputedStyle(wrapper).overflowY !== 'visible' ? wrapper : null;
  const near = new IntersectionObserver((entries) => entries.at(-1).isIntersecting && load(), { root, rootMargin: '800px' });
  const load = async () => {
    if (box.dataset.busy) return;
    box.dataset.busy = '1';
    button.textContent = 'Loading...';
    try {
      const url = new URL(box.dataset.next, location.origin);
      url.searchParams.set('section_id', box.dataset.section);
      const doc = new DOMParser().parseFromString(await (await fetch(url)).text(), 'text/html');
      grid.append(...doc.querySelectorAll('.resource-list__item'));
      const next = doc.querySelector('.load-more');
      if (!next) return box.remove();
      box.dataset.next = next.dataset.next;
      // The browser keeps the screen where it was, so the button can still be near it: observe again to recheck.
      near.unobserve(box);
      near.observe(box);
    } catch (error) {
      console.warn('Load more failed', error); // the button stays, so the visitor can try again
    }
    button.textContent = 'Load more';
    delete box.dataset.busy;
  };
  button.addEventListener('click', load);
  near.observe(box);
});

// Home art wall (sections/caracal-art-wall.liquid): the strip of artworks slides by itself. A swipe, a drag of the
// scrollbar or the wheel takes over; it slides on 2 seconds after the visitor stops. The list is there twice,
// so at the halfway point it jumps back by half without a visible change.
document.querySelectorAll('.art-wall__strip').forEach((strip) => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const SPEED = 0.05; // px per ms: 50 px a second
  let pos = 0;
  let last = 0;
  let pausedUntil = 0;
  let hovered = false;
  const hold = () => (pausedUntil = performance.now() + 2000);
  ['touchstart', 'pointerdown', 'wheel'].forEach((type) => strip.addEventListener(type, hold, { passive: true }));
  // While the visitor is in control, each scroll (a swipe's glide too) keeps the pause going.
  strip.addEventListener('scroll', () => performance.now() < pausedUntil && hold(), { passive: true });
  strip.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && (hovered = true));
  strip.addEventListener('pointerleave', () => (hovered = false));
  const tiles = strip.querySelectorAll('.art-wall__tile');
  const frame = (now) => {
    const half = tiles[tiles.length / 2].offsetLeft - tiles[0].offsetLeft; // one copy of the list, gap included
    if (now < pausedUntil || hovered) {
      pos = strip.scrollLeft;
    } else {
      pos += Math.min(now - last, 50) * SPEED;
      if (pos >= half) pos -= half;
      strip.scrollTo({ left: pos, behavior: 'instant' }); // the theme's smooth scrolling would swallow each small step
    }
    last = now;
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
});

// Swipeable rows and the art strip: the browser lazy-loads a picture only once it is on screen sideways too, so
// swiped-in cards showed empty first. When a row comes within 600 px of the screen, all its pictures start loading.
{
  const wrapper = document.querySelector('.page-wrapper');
  const root = wrapper && getComputedStyle(wrapper).overflowY !== 'visible' ? wrapper : null; // see "Load more" above
  const near = new IntersectionObserver(
    (entries) => {
      for (const { isIntersecting, target } of entries) {
        if (!isIntersecting) continue;
        target.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.sizes = img.sizes.replace(/^auto,\s*/, ''); // "auto" works only for lazy pictures
          img.loading = 'eager';
        });
        near.unobserve(target);
      }
    },
    { root, rootMargin: '600px 0px' }
  );
  document.querySelectorAll('.resource-list__carousel, .art-wall__strip').forEach((row) => near.observe(row));
}
